import React, { useState } from 'react';
import { Card } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useBreakpoint, useCommon } from '@hooks';
import * as Routes from '@constants/routes';
import { isCountryPeruOrColombia } from '@constants/regions';
import { ConfirmationModal } from '@components';
import { moneyFormat, normalizeValue } from '@helpers';
import InputText from '@components/Inputs/InputCurrency';
import GenericForm from '@components/Forms/GenericForm';
import { Radio, Button as ButtonFpay } from 'fpay-ui';
import styled, { css } from 'styled-components';
import parse from 'html-react-parser';
import { getEnv } from '@config';

import { useDispatch } from 'react-redux';
import { showError } from '@store/logged/actions';
import { setAmountWithraw } from '@store/andes/actions';
import { isAllowed } from '@helpers/auth';
import { withdrawalLimit } from '@constants/documentation';
import schema from './schema';
import classes from './Withdrawals.module.scss';

const StyledRadio = styled(Radio)(
  ({ theme }) => `
    padding-top: 11px;
    padding-bottom: 8px;
    & label {
      display: flex;
      flex-direction: row-reverse;
      span {
        margin-left: 4px;
      }
    }
    & span::before {
      box-sizing: initial;
      top: 2px;
    }
    & span[disabled]:before {
      border: 1px solid ${theme.colors.gray600};
    }
    & span::after {
      box-sizing: initial;
      top: 6px;
    }
    & span[disabled]:after {
      background: ${theme.colors.gray600};
    }
  `
);

// TODO: Refactor BreakPoints to eliminate @media
const Button = styled(ButtonFpay)(
  ({ type }) => css`
    padding: 12px 24px 11px;
    border-radius: 40px;
    @media (max-width: 600px) {
      padding: 12px 0 11px;
      margin: 0;
      position: absolute;
      bottom: 100px;
      ${type === 'submit'
        ? css`
            right: 0;
          `
        : css`
            left: 0;
          `}
      right: 0;
      width: calc(50% - 8px) !important;
    }
  `
);

const SpaceChildren = styled.div`
  border-top: solid 1px ${({ theme }) => theme.colors.gray400};
  padding-top: 24px;
  margin: 0 0 56px;
  div {
    padding: 0;
  }
`;

const Withdrawals = ({
  balances,
  handleWithdraw,
  handleChange,
  bankAccount,
  hasError,
  childComponent,
  amount,
  isValidAmount,
  htmlIdButtonSuccess = 'unidentified-id',
  htmlIdButtonCancel = 'unidentified-id',
  htmlIdRadioButton = 'unidentified-id',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { mobile } = useBreakpoint();
  const textAvailable = mobile
    ? t('paymentsFoundsOut.common.available')
    : t('paymentsFoundsOut.common.altAvailable');
  const [withdrawAll, setWithdrawAll] = useState('0');
  const { setModal } = useCommon();
  const { country } = getEnv();
  const isPeru = country === 'pe';
  const isColombia = country === 'co';
  const [lowAmountPe, setLowAmountPe] = useState(false);
  const [exceedLimitDaily, setExceedLimitDaily] = useState(false);
  const [amountMessageError, setAmountMessageError] = useState('');

  let bankID = '';

  if (bankAccount) bankID = bankAccount.bank_data.finantial_institution_id;

  const isBankBCP = isPeru && bankID === '002';
  const isBankDavivienda = isColombia && bankID === '000051';

  const handlewithdrawAll = () => {
    const amountToShow =
      balances.available > balances.dayLimit
        ? normalizeValue(balances.dayLimit)
        : normalizeValue(balances.available);
    handleChange({
      target: {
        value: withdrawAll === '0' ? amountToShow : 0,
        id: 'amount',
      },
    });
    setWithdrawAll((state) => (state === '0' ? '1' : '0'));
  };

  const money = moneyFormat(balances.dayLimit, true);
  let maxAmountText = t('paymentsFoundsOut.common.descriptionFoundsOutWithoutDocument', {
    money,
    hoursTime: isBankBCP ? '48' : '72',
    hoursTimeCo: isBankDavivienda ? '24' : '48',
  });

  if (balances.dayLimit === withdrawalLimit.fullProfile) {
    if (isAllowed('WITHDRAW_LARGE_AMOUNT')) {
      maxAmountText = t('paymentsFoundsOut.automaticFundsTransfer.maxAmountText', {
        money,
      });
    } else {
      maxAmountText = parse(t('paymentsFoundsOut.common.descriptionFoundsOutWithDocument', {
        money,
        hoursTime: isBankBCP ? '48' : '72',
        hoursTimeCo: isBankDavivienda ? '24' : '48',
      }));
    }
  }

  const handleOnSubmit = () => {
    if (!isValidAmount(amount))
      return dispatch(showError({ message: t('paymentsFoundsOut.errors.available') }));

    if (isPeru && amount < 1) {
      setAmountMessageError('');
      return setLowAmountPe(true);
    }

    // TODO: used in hook
    const useDoCashOut = isCountryPeruOrColombia;

    if (
      !useDoCashOut &&
      isAllowed('WITHDRAW_LARGE_AMOUNT') &&
      amount > withdrawalLimit.fullProfile
    ) {
      setModal({
        body: (
          <ConfirmationModal
            title="paymentsFoundsOut.automaticFundsTransfer.confirmModal"
            handleConfirm={handleWithdraw}
            confirmTitle="paymentsFoundsOut.common.continue"
          />
        ),
      });
    } else {
      if (isAllowed('CHECKOUT_FUNDS_OUT')) {
        dispatch(setAmountWithraw(amount));
        history.push(Routes.FOUNDS_OUT_CHECK);
      } else {
        handleWithdraw();
      }
    }
  };

  const disabledPe = isPeru && amount === 0;

  const checkAmountDailyLimit = (value) => {
    if (value > balances.dayLimit) {
      setExceedLimitDaily(true);
      setAmountMessageError(t('paymentsFoundsOut.common.messageErrorExceedsMaximum'));
    } else if (value < 1) {
      setExceedLimitDaily(true);
      setAmountMessageError(t('paymentsFoundsOut.common.messageErrorEnterValidAmount'));
    } else {
      setExceedLimitDaily(false);
      setAmountMessageError('');
    }
  };

  return (
    <>
      <Card className={['popup_card', classes.card].join(' ')}>
        <h3 className={classes.titleWithdrawals}>{t('paymentsFoundsOut.common.titleFoundsOut')}</h3>
        <h4 data-testid="withdrawals_description" className={classes.subTitleWithdrawals}>
          {bankID === '' ? t('paymentsFoundsOut.common.noBankAccount') : maxAmountText}
        </h4>
        <div className={classes.available}>
          <span className={classes.availableBalance}>{textAvailable}</span>
          <span className={classes.amount}>
            {balances.available == null
              ? t('paymentsFoundsOut.common.error')
              : moneyFormat(balances.available)}
          </span>
        </div>
        <GenericForm
          container
          justify="flex-end"
          alignItems="center"
          spacing={2}
          schema={schema}
          onSubmit={handleOnSubmit}
        >
          <InputText
            label={t('common.amount')}
            id="amount"
            value={amount}
            fullWidth
            onChange={(_event, value) => {
              handleChange({
                target: {
                  value,
                  id: 'amount',
                },
              });
              setWithdrawAll('0');
              checkAmountDailyLimit(value);
            }}
            variant="outlined"
            gridProps={{ xs: 12, item: true }}
            error={lowAmountPe || exceedLimitDaily}
            helperTextError={lowAmountPe || exceedLimitDaily}
            helperTextValue={amountMessageError}
          />

          {isAllowed('WITHDRAW_ALL') ? (
            <Radio
              id={htmlIdRadioButton}
              name="withdrawl_all"
              testId="withdrawl_all"
              gridProps={{ xs: 12, item: true }}
              options={[{ value: '1', label: t('paymentsFoundsOut.common.withdrawMaximun') }]}
              onChange={() => {}}
              onClick={handlewithdrawAll}
              value={withdrawAll}
              className={classes.withdrawlRadio}
            />
          ) : (
            <></>
          )}
          <Button
            id={htmlIdButtonCancel}
            testId="button-cancel"
            gridProps={{ item: true }}
            type="outlined"
            color="black"
            onClick={() => {
              history.push(Routes.FUNDS_OUT);
            }}
          >
            {t('paymentsQr.commercePortal.generateQR.buttonCancel')}
          </Button>
          <Button
            id={htmlIdButtonSuccess}
            testId="button-success"
            type="submit"
            gridProps={{ item: true }}
            cardButton
            disabled={hasError || !bankAccount || bankID === '' || disabledPe}
          >
            {t('paymentsFoundsOut.common.buttonFoundsOut')}
          </Button>
        </GenericForm>
        {mobile && <SpaceChildren>{childComponent}</SpaceChildren>}
      </Card>
      {!mobile && <div className={classes.childCard}>{childComponent}</div>}
    </>
  );
};
export default Withdrawals;
